import {ScanRepository, UpdateDeliveryRequest, CreateAssignmentRequest} from '../../domain/scan/repositories/ScanRepository';
import {Service} from '../../domain/scan/entities/Service';
import {Beneficiary} from '../../domain/scan/entities/Beneficiary';
import {BenefitDetail} from '../../domain/scan/entities/BenefitDetail';
import {AssignmentDetail} from '../../domain/scan/entities/AssignmentDetail';
import {Delivery, CreateDeliveryRequest} from '../../domain/scan/entities/Delivery';
import {seliApiClient, sociantApiClient, ApiError} from '../../shared/http/apiClient';
import {AsyncStorageAdapter} from '../storage/AsyncStorageAdapter';
import {mapEligibilityItemsToServices} from '../../shared/mappers/eligibilityMapper';
import {mapBenefitDetailResponse} from '../../shared/mappers/benefitDetailMapper';
import {mapAssignmentDetailResponse} from '../../shared/mappers/assignmentDetailMapper';
import {mapDeliveryResponse} from '../../shared/mappers/deliveryMapper';

export class ScanRepositoryImpl implements ScanRepository {
  private storageAdapter: AsyncStorageAdapter;

  constructor() {
    this.storageAdapter = new AsyncStorageAdapter();
  }

  async scanQRCode(qrCode: string): Promise<Beneficiary> {
    // QR codes encode only the case_user_id as a plain integer string.
    // No API call needed ? parse the value directly.
    const caseUserId = parseInt(qrCode.trim(), 10);
    if (isNaN(caseUserId) || caseUserId <= 0) {
      throw new Error('C?digo QR no encontrado o inv?lido');
    }
    return {
      id: caseUserId.toString(),
      name: `Caso #${caseUserId}`,
      documentNumber: '',
      email: undefined,
      phone: undefined,
      photoUrl: undefined,
      caseId: caseUserId,
    };
  }

  async getAssignmentsByCaseUser(caseUserId: number): Promise<any> {
    try {
      // Get auth token
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Call GET /api/benefits_managment/assignments?case_user_id=<case_user_id>&status=2 (Active)
      const response = await seliApiClient.get<any>(
        `/api/benefits_managment/assignments?case_user_id=${caseUserId}&status=2&page=1&per_page=100`,
        {
          Authorization: `Bearer ${token}`,
        },
      );
      return response.data;
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error fetching assignments by case user:', apiError);
      throw new Error(
        apiError.message ||
          apiError.data?.message ||
          'Error al obtener las asignaciones del caso',
      );
    }
  }

  async getAvailableServicesFromEligibility(caseUserId: number): Promise<Service[]> {
    try {
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await seliApiClient.get<any>(
        `/api/benefits_managment/elegibility/by-case-user/${caseUserId}`,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      const items = response.data?.items || [];
      return mapEligibilityItemsToServices(items);
    } catch (error: any) {
      console.error('Error getting services from eligibility:', error);
      throw error;
    }
  }


  async createAssignment(request: CreateAssignmentRequest): Promise<{id: number}> {
    try {
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;
      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await seliApiClient.post<any>(
        '/api/benefits_managment/assignments',
        {
          benefit_id: request.benefitId,
          case_user_id: request.caseUserId,
          recurrence: request.recurrence,
          start_date: request.startDate,
        },
        {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      );

      return {id: response.data.id};
    } catch (error: any) {
      const apiError = error as ApiError;
      throw new Error(
        apiError.message || apiError.data?.message || 'Error al crear la asignación',
      );
    }
  }

  async updateRedemptionStatus(redemptionId: number, status: string): Promise<any> {
    try {
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;
      if (!token) {
        throw new Error('No authentication token available');
      }

      const requestBody = {status: status.toUpperCase()};

      const response = await seliApiClient.put<any>(
        `/api/benefits_managment/deliveries/${redemptionId}`,
        requestBody,
        {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      );

      return response.data;
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error updating redemption status:', apiError);

      if (apiError.status === 400) {
        throw new Error(
          apiError.data?.message ||
            apiError.message ||
            'Datos inv?lidos para actualizar el estado',
        );
      } else if (apiError.status === 401 || apiError.status === 403) {
        throw new Error('No autorizado. Por favor inicia sesi?n nuevamente.');
      } else if (apiError.status === 404) {
        throw new Error('Canje no encontrado');
      } else {
        throw new Error(
          apiError.message ||
            apiError.data?.message ||
            'Error al actualizar el estado del canje',
        );
      }
    }
  }

  async getBenefitDetail(programId: string, benefitId: string): Promise<BenefitDetail> {
    try {
      // Get auth token
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Call GET /api/benefits_managment/benefits/:program_id/:benefit_id
      const response = await seliApiClient.get<any>(
        `/api/benefits_managment/benefits/${programId}/${benefitId}`,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      // Map response to BenefitDetail entity
      return mapBenefitDetailResponse(response.data);
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error fetching benefit detail:', apiError);

      if (apiError.status === 404) {
        throw new Error('Beneficio no encontrado');
      } else if (apiError.status === 401 || apiError.status === 403) {
        throw new Error('No autorizado. Por favor inicia sesi?n nuevamente.');
      } else {
        throw new Error(
          apiError.message ||
            apiError.data?.message ||
            'Error al obtener el detalle del beneficio',
        );
      }
    }
  }

  async getAssignmentById(assignmentId: number): Promise<AssignmentDetail> {
    try {
      // Get auth token
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Call GET /api/benefits_managment/assignments/:assignment_id
      const response = await seliApiClient.get<any>(
        `/api/benefits_managment/assignments/${assignmentId}`,
        {
          Authorization: `Bearer ${token}`,
        },
      );
      // Map response to AssignmentDetail entity
      return mapAssignmentDetailResponse(response.data);
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error fetching assignment by ID:', apiError);

      if (apiError.status === 404) {
        throw new Error('Asignaci?n no encontrada');
      } else if (apiError.status === 401 || apiError.status === 403) {
        throw new Error('No autorizado. Por favor inicia sesi?n nuevamente.');
      } else {
        throw new Error(
          apiError.message ||
            apiError.data?.message ||
            'Error al obtener el detalle de la asignaci?n',
        );
      }
    }
  }

  async createDelivery(request: CreateDeliveryRequest): Promise<Delivery> {
    try {
      // Get auth token
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Prepare request body
      const requestBody = {
        assignment_id: request.assignmentId,
        benefit_id: request.benefitId,
        case_user_id: request.caseUserId,
        delivery_amount: request.deliveryAmount,
      };

      // Call POST /api/benefits_managment/deliveries
      const response = await seliApiClient.post<any>(
        '/api/benefits_managment/deliveries',
        requestBody,
        {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      );
      
      // Map response to Delivery entity
      return mapDeliveryResponse(response.data);
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error creating delivery:', apiError);

      if (apiError.status === 400) {
        throw new Error(
          apiError.data?.message ||
            apiError.message ||
            'Datos inv?lidos para crear el delivery',
        );
      } else if (apiError.status === 401 || apiError.status === 403) {
        throw new Error('No autorizado. Por favor inicia sesi?n nuevamente.');
      } else if (apiError.status === 404) {
        throw new Error('Assignment o beneficio no encontrado');
      } else {
        throw new Error(
          apiError.message ||
            apiError.data?.message ||
            'Error al crear el delivery',
        );
      }
    }
  }

  async updateDelivery(request: UpdateDeliveryRequest): Promise<Delivery> {
    try {
      // Get auth token
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Build request body with only provided fields
      const requestBody: any = {};
      
      if (request.status !== undefined) {
        requestBody.status = request.status;
      }
      
      if (request.action) {
        requestBody.action = request.action;
      }
      
      if (request.deliveredDate) {
        requestBody.delivered_date = request.deliveredDate;
      }
      
      if (request.notes !== undefined) {
        requestBody.notes = request.notes;
      }

      // Call PUT /api/benefits_managment/deliveries/:delivery_id
      const response = await seliApiClient.put<any>(
        `/api/benefits_managment/deliveries/${request.deliveryId}`,
        requestBody,
        {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      );

      // Map response to Delivery entity
      return mapDeliveryResponse(response.data);
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error updating delivery:', apiError);

      if (apiError.status === 400) {
        throw new Error(
          apiError.data?.message ||
            apiError.message ||
            'Datos inv?lidos para actualizar el delivery',
        );
      } else if (apiError.status === 401 || apiError.status === 403) {
        throw new Error('No autorizado. Por favor inicia sesi?n nuevamente.');
      } else if (apiError.status === 404) {
        throw new Error('Delivery no encontrado');
      } else {
        throw new Error(
          apiError.message ||
            apiError.data?.message ||
            'Error al actualizar el delivery',
        );
      }
    }
  }

  async getDeliveryById(deliveryId: number): Promise<Delivery> {
    try {
      // Get auth token
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      // Call GET /api/benefits_managment/deliveries/:delivery_id
      const response = await seliApiClient.get<any>(
        `/api/benefits_managment/deliveries/${deliveryId}`,
        {
          Authorization: `Bearer ${token}`,
        },
      );
      // Map response to Delivery entity
      return mapDeliveryResponse(response.data);
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error fetching delivery by ID:', apiError);

      if (apiError.status === 404) {
        throw new Error('Delivery no encontrado');
      } else if (apiError.status === 401 || apiError.status === 403) {
        throw new Error('No autorizado. Por favor inicia sesi?n nuevamente.');
      } else {
        throw new Error(
          apiError.message ||
            apiError.data?.message ||
            'Error al obtener el detalle del delivery',
        );
      }
    }
  }

  async getCaseDetail(caseId: number): Promise<import('../../domain/scan/entities/CaseDetail').CaseDetail> {
    try {
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await seliApiClient.get<any>(
        `/api/benefits_managment/case-detail/${caseId}`,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      // Map to CaseDetail entity (now includes photo_url from unified endpoint)
      return {
        id: response.data.id,
        formattedId: response.data.formatted_id,
        firstName: response.data.first_name,
        lastName: response.data.last_name,
        dateOfBirth: response.data.date_of_birth,
        age: response.data.age,
        gender: response.data.gender,
        phoneNumber: response.data.phone_number,
        email: response.data.email,
        altPhoneNumber: response.data.alt_phone_number,
        organizationId: response.data.organization_id,
        caseType: response.data.case_type,
        status: response.data.status,
        createdDate: response.data.created_date,
        photoUrl: response.data.photo_url, // Photo URL included in response
      };
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error fetching case detail:', apiError);

      if (apiError.status === 404) {
        throw new Error('Caso no encontrado');
      } else if (apiError.status === 401 || apiError.status === 403) {
        throw new Error('No autorizado. Por favor inicia sesi?n nuevamente.');
      } else {
        throw new Error(
          apiError.message ||
            apiError.data?.message ||
            'Error al obtener el detalle del caso',
        );
      }
    }
  }

  async getOrganizationDocument(documentId: number): Promise<import('../../domain/scan/entities/CaseDetail').OrganizationDocument> {
    try {
      const user = await this.storageAdapter.getUser();
      const token = user?.token || null;

      if (!token) {
        throw new Error('No authentication token available');
      }

      const response = await sociantApiClient.get<any>(
        `/api/organization/document/v2/${documentId}`,
        {
          Authorization: `Bearer ${token}`,
        },
      );

      return {
        preview: response.data.preview,
        type: response.data.type,
      };
    } catch (error: any) {
      const apiError = error as ApiError;
      console.error('Error fetching organization document:', apiError);

      if (apiError.status === 404) {
        throw new Error('Documento no encontrado');
      } else if (apiError.status === 401 || apiError.status === 403) {
        throw new Error('No autorizado. Por favor inicia sesi?n nuevamente.');
      } else {
        throw new Error(
          apiError.message ||
            apiError.data?.message ||
            'Error al obtener el documento',
        );
      }
    }
  }
}

